import { Controller } from 'stimulus';

const NAV_LINK_SELECTOR = '.navbar-nav > li > a';
const ASYNC_TASKS_TRIGGER_SELECTOR = '.async-tasks-trigger';

export default class extends Controller {
  connect() {
    const btn = this.element.querySelector('.navbar-sidebar-toggle');
    if (btn) {
      this._attachTooltip(btn, () => (
        document.documentElement.classList.contains('sidebar-expanded') ? btn.dataset.collapseTitle : btn.dataset.expandTitle
      ), { offset: 8, rtlOffset: -4 });
    }

    this.element.querySelectorAll('[data-sidebar-tooltip]').forEach((el) => {
      const label = el.dataset.sidebarTooltip;
      const subtitle = el.dataset.sidebarTooltipSubtitle;
      this._attachTooltip(el, () => label, { getSubtitle: () => subtitle });
    });

    if (!document.documentElement.classList.contains('sidebar-expanded')) this._initNavTooltips();
  }

  toggle(event) {
    const expanded = document.documentElement.classList.toggle('sidebar-expanded');
    document.cookie = `sidebar_expanded=${expanded ? '1' : '0'};path=/;max-age=31536000;SameSite=Lax`;
    if (event.currentTarget._navTip) event.currentTarget._navTip.remove();

    if (expanded) {
      this._destroyNavTooltips();
    } else {
      this._initNavTooltips();
    }
  }

  _initNavTooltips() {
    this.element.querySelectorAll(NAV_LINK_SELECTOR).forEach((link) => {
      const label = link.querySelector('span:not(.badge)') && link.querySelector('span:not(.badge)').textContent.trim();
      if (label) this._attachTooltip(link, () => label, { hideOnClick: true });
    });
    this.element.querySelectorAll(ASYNC_TASKS_TRIGGER_SELECTOR).forEach((trigger) => {
      const labelEl = trigger.querySelector('.async-tasks-label');
      const label = labelEl && labelEl.textContent.trim();
      if (label) this._attachTooltip(trigger, () => label, { hideOnClick: true });
    });
  }

  _destroyNavTooltips() {
    this.element.querySelectorAll(NAV_LINK_SELECTOR).forEach((link) => {
      this._detachTooltip(link);
    });
    this.element.querySelectorAll(ASYNC_TASKS_TRIGGER_SELECTOR).forEach((trigger) => {
      this._detachTooltip(trigger);
    });
  }

  _attachTooltip(element, getLabel, {
    hideOnClick = false, offset = 14, rtlOffset = 2, getSubtitle = () => null,
  } = {}) {
    const show = () => {
      const label = getLabel();
      if (!label) return;
      const rect = element.getBoundingClientRect();
      const isRtl = document.documentElement.dir === 'rtl';
      const tip = document.createElement('div');
      tip.className = 'navbar-sidebar-nav-tooltip';
      const subtitle = getSubtitle();
      if (subtitle) {
        const title = document.createElement('div');
        title.textContent = label;
        const sub = document.createElement('div');
        sub.className = 'navbar-sidebar-nav-tooltip__subtitle';
        sub.textContent = subtitle;
        tip.append(title, sub);
      } else {
        tip.textContent = label;
      }
      tip.style.top = `${rect.top + rect.height / 2}px`;
      if (isRtl) {
        tip.style.right = `${window.innerWidth - rect.left + rtlOffset}px`;
      } else {
        tip.style.left = `${rect.right + offset}px`;
      }
      document.body.appendChild(tip);
      element._navTip = tip;
    };

    const hide = () => {
      if (element._navTip) element._navTip.remove();
      element._navTip = null;
    };

    element.addEventListener('mouseenter', show);
    element.addEventListener('mouseleave', hide);
    if (hideOnClick) element.addEventListener('click', hide);
    element._navTipShow = show;
    element._navTipHide = hide;
    element._navTipHideOnClick = hideOnClick;
  }

  _detachTooltip(element) {
    if (element._navTip) element._navTip.remove();
    element._navTip = null;
    if (element._navTipShow) element.removeEventListener('mouseenter', element._navTipShow);
    if (element._navTipHide) {
      element.removeEventListener('mouseleave', element._navTipHide);
      if (element._navTipHideOnClick) element.removeEventListener('click', element._navTipHide);
    }
  }

  expandAndFocus() {
    document.documentElement.classList.add('sidebar-expanded');
    document.cookie = 'sidebar_expanded=1;path=/;max-age=31536000;SameSite=Lax';
    this._destroyNavTooltips();
    const input = this.element.querySelector('.navbar-sidebar-search-form input');
    if (input) {
      const onEnd = () => {
        input.focus();
        this.element.removeEventListener('transitionend', onEnd);
      };
      this.element.addEventListener('transitionend', onEnd);
    }
  }

  persistSearchValue(event) {
    event.target.setAttribute('value', event.target.value);
  }

  resetDropdownArrow(event) {
    event.currentTarget.closest('li').style.removeProperty('--arrow-top');
  }

  positionDropdown(event) {
    const li = event.currentTarget.closest('li');
    const menu = li.querySelector('.dropdown-menu');
    if (!menu) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const centerY = rect.top + rect.height / 2;

    menu.style.top = `${centerY}px`;
    menu.style.bottom = 'auto';
    menu.style.transform = 'translateY(-50%)';
    li.style.setProperty('--arrow-top', `${centerY}px`);

    requestAnimationFrame(() => {
      const menuHeight = menu.offsetHeight;
      if (!menuHeight) return;

      const minTop = 10 + menuHeight / 2;
      const maxTop = window.innerHeight - 10 - menuHeight / 2;
      const top = Math.min(Math.max(centerY, minTop), maxTop);
      menu.style.top = `${top}px`;
    });
  }
}
